<template>
  <div class="min-h-screen flex items-center justify-center bg-muted/30 px-4 py-12">
    <UiCard class="w-full max-w-md">
      <UiCardHeader class="space-y-1 text-center">
        <UiCardTitle>{{ $t('auth.forgotPassword.title') }}</UiCardTitle>
        <UiCardDescription>{{ $t('auth.forgotPassword.description') }}</UiCardDescription>
      </UiCardHeader>
      <UiCardContent class="space-y-4">
        <!-- Success Alert -->
        <UiAlert v-if="success" variant="success">
          <Icon name="tabler:circle-check" size="16" />
          <UiAlertDescription>{{ $t('auth.forgotPassword.successMessage') }}</UiAlertDescription>
        </UiAlert>

        <!-- Error Alert -->
        <UiAlert v-if="error" variant="destructive">
          <Icon name="tabler:alert-circle" size="16" />
          <UiAlertDescription>{{ error }}</UiAlertDescription>
        </UiAlert>

        <!-- Forgot Password Form -->
        <form v-if="!success" @submit.prevent="handleForgotPassword" class="space-y-4">
          <div class="space-y-2">
            <UiLabel for="email">{{ $t('auth.fields.email') }}</UiLabel>
            <UiInput
              id="email"
              v-model="email"
              type="email"
              :placeholder="$ts('auth.fields.emailPlaceholder')"
              required
              autocomplete="email"
            />
          </div>
          <UiButton type="submit" class="w-full" :disabled="isLoading">
            <Icon v-if="isLoading" name="tabler:loader-2" size="16" class="animate-spin" />
            {{ $t('auth.forgotPassword.submit') }}
          </UiButton>
        </form>

        <!-- Back to Login -->
        <div v-if="success" class="text-center">
          <NuxtLink :to="$localeRoute('/account/login')">
            <UiButton variant="outline" class="w-full">
              <Icon name="tabler:arrow-left" size="16" />
              {{ $t('auth.forgotPassword.backToLogin') }}
            </UiButton>
          </NuxtLink>
        </div>
      </UiCardContent>
      <UiCardFooter v-if="!success" class="flex justify-center">
        <p class="text-sm text-muted-foreground">
          {{ $t('auth.forgotPassword.rememberPassword') }}
          <NuxtLink :to="$localeRoute('/account/login')" class="text-primary hover:underline">
            {{ $t('auth.forgotPassword.signIn') }}
          </NuxtLink>
        </p>
      </UiCardFooter>
    </UiCard>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { definePageMeta, useI18n, useSeoMeta } from '#imports';
import { useAuthClient } from '#composables/useAuthClient';

definePageMeta({
  middleware: ['guest'],
});

const { $ts, $localeRoute } = useI18n();
useSeoMeta({ title: () => $ts('auth.forgotPassword.seo.title') });

const authClient = useAuthClient();

const email = ref('');
const isLoading = ref(false);
const error = ref<string | null>(null);
const success = ref(false);

async function handleForgotPassword() {
  isLoading.value = true;
  error.value = null;

  try {
    const result = await authClient.requestPasswordReset({
      email: email.value,
      redirectTo: '/account/reset-password',
    });

    if (result.error) {
      error.value = result.error.message || 'Failed to send reset email. Please try again.';
      return;
    }

    success.value = true;
  } catch (e: unknown) {
    error.value = e instanceof Error ? e.message : 'An unexpected error occurred.';
  } finally {
    isLoading.value = false;
  }
}
</script>
